import { X, ShoppingCart, Plus, Minus, Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  getCart,
  removeFromCart,
  clearCart,
  updateQuantity,
  getCartTotal,
  getCartCount
} from "../../utils/CartUtils";
import { useNotification } from "../../context/NotificationContext";
import { formatPrice } from "../../utils/productUtils";

function SkeletonCartItem() {
  return (
    <div className="flex gap-4 p-4 bg-white dark:bg-[#1E1E1E] rounded-xl border border-[#E9E9E9] dark:border-[#333333]">
      <div className="w-20 h-20 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded-lg animate-shimmer"></div>
      <div className="flex-1 space-y-2">
        <div className="h-4 w-3/4 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
        <div className="h-3 w-1/2 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
        <div className="h-5 w-24 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
      </div>
      <style jsx="true">{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default function Cart({ isOpen, onClose }) {
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false); // Reduced legacy loading
  const [cartItems, setCartItems] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const refreshCart = useCallback(() => {
    const items = getCart();
    setCartItems(items);
  }, []);

  // Sync cart state on open and on custom event
  useEffect(() => {
    if (isOpen) {
      refreshCart();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, refreshCart]);

  useEffect(() => {
    window.addEventListener("cartUpdated", refreshCart);
    window.addEventListener("storage", refreshCart);
    return () => {
      window.removeEventListener("cartUpdated", refreshCart);
      window.removeEventListener("storage", refreshCart);
    };
  }, [refreshCart]);

  const handleUpdateQuantity = (id, newQuantity) => {
    const result = updateQuantity(id, newQuantity);
    if (!result.success && result.message) {
      showNotification({
        type: "warning",
        title: "Quantity Limit",
        message: result.message
      });
    }
  };

  const handleRemoveItem = (id) => {
    removeFromCart(id);
    showNotification({
      type: "info",
      message: "Item removed from cart"
    });
  };

  const handleClearCart = () => {
    setShowClearConfirm(true);
  };

  const confirmClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
    showNotification({
      type: "success",
      message: "Cart cleared successfully"
    });
  };

  const totalAmount = getCartTotal();
  const totalItemsCount = getCartCount();

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={onClose}
      ></div>

      {/* Cart Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[450px] lg:w-[400px] bg-white dark:bg-[#1E1E1E] shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E9E9E9] dark:border-[#333333] bg-gradient-to-br from-[#62397f] via-[#7C3AED] to-[#6D28D9]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <ShoppingCart size={20} className="text-white" />
            </div>
            <div>
              <h2
                className="text-xl font-medium font-serif text-white"
              >
                My Rentals
              </h2>
              {cartItems.length > 0 && (
                <p className="text-sm text-white/80">
                  {totalItemsCount} {totalItemsCount === 1 ? "item" : "items"}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <SkeletonCartItem key={index} />
              ))}
            </div>
          ) : cartItems.length === 0 ? (
            // Empty State
            <div className="flex flex-col items-center justify-center h-full py-12">
              <div className="w-32 h-32 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-6">
                <ShoppingCart
                  size={48}
                  className="text-[#62397f] dark:text-[#A78BFA]"
                />
              </div>
              <h3
                className="text-2xl font-medium font-serif text-black dark:text-white mb-3 text-center"
              >
                Your Cart is Empty
              </h3>
              <p className="text-[#666666] dark:text-[#A0A0A0] text-center mb-8 max-w-xs">
                Start adding rental items to your cart and they'll appear here.
              </p>
              <Link
                onClick={onClose}
                to="/rentals"
                className="px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02]"
              >
                Browse Rentals
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Clear Cart Button */}
              <div className="flex justify-end mb-2">
                <button
                  onClick={handleClearCart}
                  className="text-sm text-[#62397f] dark:text-[#A78BFA] hover:underline font-medium"
                >
                  Clear All
                </button>
              </div>

              {/* Cart Items */}
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white dark:bg-[#1A1A1A] rounded-2xl p-4 border border-[#E9E9E9] dark:border-[#333333] transition-all duration-200 hover:shadow-lg"
                >
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="relative w-20 h-20 flex-shrink-0 bg-[#F5F5F5] dark:bg-[#2A2A2A] rounded-lg overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[#62397f] dark:text-[#A78BFA] uppercase tracking-wider mb-1">
                            {item.category}
                          </p>
                          <h3 className="font-serif text-sm md:text-base font-bold text-black dark:text-white truncate">
                            {item.name}
                          </h3>
                        </div>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-[#999999] hover:text-red-500 dark:text-[#666666] dark:hover:text-red-400 transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Price and Quantity */}
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-base font-bold text-[#62397f] dark:text-[#A78BFA]">
                            ₦{formatPrice(item.quantity * item.price)}
                          </span>
                          <span className="text-xs text-[#999999] dark:text-[#666666]">
                            ₦{formatPrice(item.price)} {item.unit}
                          </span>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 bg-[#F5F5F5] dark:bg-[#2A2A2A] hover:bg-[#E9E9E9] dark:hover:bg-[#333333] rounded-lg flex items-center justify-center transition-colors"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="w-8 text-center text-sm font-bold text-black dark:text-white">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                            disabled={item.quantity >= item.qtyAvailable}
                            className="w-8 h-8 bg-[#F5F5F5] dark:bg-[#2A2A2A] hover:bg-[#E9E9E9] dark:hover:bg-[#333333] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg flex items-center justify-center transition-colors"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Availability Warning */}
                      {item.quantity >= item.qtyAvailable && (
                        <p className="text-[10px] text-orange-500 dark:text-orange-400 mt-2">
                          Maximum available: {item.qtyAvailable}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Checkout Section */}
        {cartItems.length > 0 && (
          <div className="border-t border-[#E9E9E9] dark:border-[#333333] p-6 bg-[#FAFAFA] dark:bg-[#1A1A1A]">
            {/* Subtotal */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#666666] dark:text-[#A0A0A0]">
                  Subtotal
                </span>
                <span className="text-sm font-semibold text-black dark:text-white">
                  ₦{formatPrice(totalAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#666666] dark:text-[#A0A0A0]">
                  Service Fee
                </span>
                <span className="text-sm font-semibold text-black dark:text-white">
                  ₦{formatPrice(0)}
                </span>
              </div>
              <div className="border-t border-[#E9E9E9] dark:border-[#333333] my-3"></div>
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-black dark:text-white">
                  Total
                </span>
                <span className="text-2xl font-bold text-[#62397f] dark:text-[#A78BFA]">
                  ₦{formatPrice(totalAmount)}
                </span>
              </div>
            </div>

            {/* Checkout Button */}
            <Link
              to="/checkout"
              onClick={onClose}
              className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#62397f] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white text-lg font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(139,92,246,0.25)] mb-3"
            >
              Proceed to Checkout
            </Link>

            <button
              onClick={onClose}
              className="w-full px-8 py-3 bg-white dark:bg-[#1E1E1E] hover:bg-[#F5F5F5] dark:hover:bg-[#2A2A2A] text-[#666666] dark:text-[#A0A0A0] font-medium rounded-full border border-[#E9E9E9] dark:border-[#333333] transition-all duration-200"
            >
              Browse Rentals
            </button>
          </div>
        )}
      </div>

      {/* Clear Cart Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowClearConfirm(false)}></div>
          <div className="relative bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold font-serif text-black dark:text-white mb-2">Clear Cart</h3>
            <p className="text-[#666666] dark:text-[#A0A0A0] text-sm mb-6">
              Are you sure you want to remove all items from your cart? This action cannot be undone.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] hover:bg-[#E9E9E9] dark:hover:bg-[#333333] text-black dark:text-white font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmClearCart}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors shadow-[0_4px_12px_rgba(239,68,68,0.25)]"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
